import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { ContractStateEnum } from '@app/core/enums/contract/contract-state.enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Contract, ContractInput } from '@app/core/model/entities/asset/contract';
import { Document } from '@app/core/model/entities/document/document';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { DocumentsService } from '@app/features/main/views/organization-documents/documents.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, switchMap } from 'rxjs';
import { map, reduce, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class ContractsService {
  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private documentsService = inject(DocumentsService);
  private assetsService = inject(AssetsService);
  private appConfig = inject(AppConfig);
  private sidePanelToggleSubject = new Subject<Contract | null>();

  private addContractSubject = new Subject<Contract>();
  private updateContractSubject = new Subject<Contract>();
  private renewContractSubject = new Subject<{ renewedContract: Contract, oldContract: Contract }>();
  private deleteContractsSubject = new Subject<Contract[]>();

  private readonly contractInfoGraphqlFragment = gql`
    fragment ContractInfo on Contract {
      id
      provider
      assetId
      spaceIdsList
      status
      properties
      computedProperties
      creationDate
      creationActor {
        actorId
        name
      }
      lastChangeDate
      lastChangeActor {
        actorId
        name
      }
      dataDate
    }
  `;

  /**
   * Loads a contracts page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch contracts based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the contracts.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Contract objects, each with additional user information.
   */
  public loadContractsPage(selectParams: SelectParams): Observable<Contract[]> {
    const query = gql`
      query OrganizationContracts($organizationId: String!, $selectParams: SelectParams!) {
        contracts(organizationId: $organizationId, selectParams: $selectParams) {
          ...ContractInfo
        }
      }
      ${this.contractInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Contract, data['contracts'] as Contract[])));
  }

  /**
   * Loads all contracts pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch contracts based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the contracts.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Contract objects, each with additional user information.
   */
  public loadAllContracts(selectParams: SelectParams): Observable<Contract[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadContractsPage(params)
      .pipe(
        expand((contracts, i) => {
          if (contracts.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadContractsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Contract[])
      );
  }

  /**
   * Emits a Contract once after it has been created.
   */
  public get contractAdded$(): Observable<Contract> {
    return this.addContractSubject.asObservable();
  }

  /**
   * Emits a Contract whenever it has been updated.
   */
  public get contractUpdated$(): Observable<Contract> {
    return this.updateContractSubject.asObservable();
  }

  /**
   * Emits a Contract after it has been renewed.
   */
  public get contractRenewed$(): Observable<{ renewedContract: Contract, oldContract: Contract }> {
    return this.renewContractSubject.asObservable();
  }

  /**
   * Emits a list of Contracts after they have been deleted.
   */
  public get contractsDeleted$(): Observable<Contract[]> {
    return this.deleteContractsSubject.asObservable();
  }

  /**
   * Emits a Contract whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Contract | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Make an API call that will create a new Contract with the provided data.
   * @param createContractInput Data for creating a new Contract.
   * @return New Contract.
   */
  public createContract(createContractInput: ContractInput): Observable<Contract> {
    const mutation = gql`
      mutation CreateContract($createContractInput: CreateContractInput!) {
        createContract(contractInput: $createContractInput) {
          ...ContractInfo
        }
      }
      ${this.contractInfoGraphqlFragment}
    `;
    const variables = {createContractInput};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Contract, data['createContract'])),
        tap(newContract => this.addContractSubject.next(newContract))
      );
  }

  /**
   * Call the API to update a Contract.
   * @param contract Contract to update.
   * @param contractInput Data for updating the Contract.
   * @return Updated Contract.
   */
  public updateContract(contract: Contract, contractInput: ContractInput): Observable<Contract> {
    const mutation = gql`
      mutation UpdateContract($contractInput: UpdateContractInput!) {
        updateContract(contractInput: $contractInput) {
          ...ContractInfo
        }
      }
      ${this.contractInfoGraphqlFragment}
    `;
    const variables = {
      contractInput: {
        id: contract.id,
        provider: contract.provider,
        spaceIds: contract.spaceIds,
        ...contractInput
      }
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Contract, data['updateContract'])),
        tap(updatedContract => this.updateContractSubject.next(updatedContract))
      );
  }

  /**
   * Call the API to update a list of Contracts.
   * @param contracts Contracts to update, each paired with the data to update it with.
   * @return Observable emitting the updated Contracts once the update is completed.
   */
  public updateContracts(contracts: {
    contract: Contract,
    contractInput: ContractInput
  }[]): Observable<Contract[]> {
    const mutation = gql`
      mutation UpdateContracts($contractInputs: [UpdateContractInput!]!) {
        updateContracts(contractInputs: $contractInputs) {
          ...ContractInfo
        }
      }
      ${this.contractInfoGraphqlFragment}
    `;
    const variables = {
      contractInputs: contracts.map(({contract: {id, provider, spaceIds}, contractInput}) => {
        return {id, provider, spaceIds, ...contractInput};
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Contract, data['updateContracts'] as Contract[])),
        switchMap(contracts => from(contracts)),
        tap(updatedContract => this.updateContractSubject.next(updatedContract)),
        toArray()
      );
  }

  /**
   * Renew an existing Contract.
   * @param contractToRenew Existing Contract to renew then close.
   * @param renewContractInput Data used to renew contract.
   * @return New Contract.
   */
  public renewContract(contractToRenew: Contract, renewContractInput: ContractInput): Observable<Contract> {
    const mutation = gql`
      mutation RenewContract($oldContractId: String!, $renewContractInput: CreateContractInput!){
        renewContract(id: $oldContractId, contractInput: $renewContractInput){
          ...ContractInfo
        }
      }
      ${this.contractInfoGraphqlFragment}
    `;
    const variables = {
      oldContractId: contractToRenew.id,
      renewContractInput: {
        assetId: contractToRenew.assetId,
        ...renewContractInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Contract, data['renewContract'])),
        tap(renewedContract => {
          contractToRenew.status = ContractStateEnum.CONTRACT_CLOSED;
          this.renewContractSubject.next({renewedContract, oldContract: contractToRenew});
        })
      );
  }

  /**
   * Make an API call to delete the given Contracts.
   * @param contracts Contracts to be deleted.
   * @return True if all Contracts were deleted, false otherwise.
   */
  public deleteContracts(contracts: Contract[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteContracts($contractIds: [String!]!) {
        deleteContracts(ids: $contractIds)
      }
    `;
    const variables = {
      contractIds: contracts.map(contract => contract.id),
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteContracts'] as boolean),
        tap(success => {
          if (success) {
            this.deleteContractsSubject.next(contracts);
          }
        })
      );
  }

  /**
   * Navigate to the contracts tab of the Asset's sheet.
   * @param assetId Id of the Asset to navigate to.
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'contracts');
  }

  /**
   * Load all the documents related to a Contract
   * @param contractId ID of Contract whose Documents should be loaded.
   * @return Documents related to the Contract.
   */
  public loadContractDocuments(contractId: string): Observable<Document[]> {
    return this.documentsService.loadEntityDocuments(
      contractId,
      EntityTypeEnum.CONTRACT,
      DocumentType.DOCUMENT
    );
  }

  /**
   * Open the sidenav with information about the selected Contract.
   * @param contract Contract to be displayed in the side panel.
   */
  public openContractSidePanel(contract: Contract): void {
    this.sidePanelToggleSubject.next(contract);
  }

  /**
   * Close the side panel.
   */
  public closeContractSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }
}
